import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import {
  Bold,
  Italic,
  UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link2,
  Unlink,
} from 'lucide-react';
import { Toggle } from '@/components/atoms/toggle';
import { cn } from '@/lib/utils';
import { RICH_TEXT_CLASS, richTextEditorContent } from '@/lib/rich-text';
import { useEffect, useCallback, useRef, useState, type ReactNode } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  ariaLabel?: string;
  id?: string;
  footer?: ReactNode;
  toolbarMode?: 'full' | 'compact';
  toolbarEnd?: ReactNode;
  /**
   * Receives the Tiptap editor instance once ready (and null on unmount), so a
   * caller can drive imperative commands — e.g. insert a token at the cursor.
   */
  onEditorReady?: (editor: Editor | null) => void;
  /** When false, the content is read-only (no toolbar edits, no typing). */
  editable?: boolean;
  /**
   * Opt-in richer schema + toolbar: text color and font family (email designer).
   * Off by default so surfaces that must round-trip to PDF/quote HTML keep the
   * constrained schema.
   */
  extended?: boolean;
  /** Base font for the content area — WYSIWYG with the delivered medium (email). */
  contentFontFamily?: string;
}

// Same choices as the estimate PDF design builder (FONT_OPTIONS there), so the
// two editors offer one consistent set. Each carries an email-safe fallback
// stack. Inter is the base font (see EMAIL_BASE_FONT) applied when no inline
// choice is made.
const EMAIL_FONTS = [
  ['Geist, sans-serif', 'Geist'],
  ['Inter, sans-serif', 'Inter'],
  ['Arial, Helvetica, sans-serif', 'Arial'],
  ['Helvetica, Arial, sans-serif', 'Helvetica'],
  ['Georgia, serif', 'Georgia'],
  ['Roboto, Arial, sans-serif', 'Roboto'],
] as const;

// Text sizes offered in the email designer. '' clears the inline size back to the
// base body size (15px). Labels stay human ('Small'…'Title'), values are px.
const EMAIL_SIZES = [
  ['', 'Normal'],
  ['13px', 'Small'],
  ['18px', 'Large'],
  ['24px', 'Title'],
] as const;

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing...',
  className,
  editorClassName,
  ariaLabel,
  id,
  footer,
  toolbarMode = 'full',
  toolbarEnd,
  onEditorReady,
  editable = true,
  extended = false,
  contentFontFamily,
}: RichTextEditorProps) {
  // Link editing: a small popover anchored to the selection (Word/Slack/Docs
  // style), opened from the toolbar or ⌘/Ctrl-K, instead of a persistent bar.
  const [linkPos, setLinkPos] = useState<{ top: number; left: number } | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  // ⌘K fires inside the editor (below), where openLinkEditor isn't defined yet;
  // call it through a ref so the handler always sees the latest closure.
  const openLinkRef = useRef<() => void>(() => {});
  const showFullToolbar = toolbarMode === 'full';

  const editor = useEditor({
    extensions: [
      // Keep the editor schema to what every surface (editor, detail/quote HTML,
      // and the PDF mapper) can render. Code/code-block and the horizontal rule
      // aren't supported downstream, so disable them rather than let paste/markdown
      // shortcuts produce nodes that would render inconsistently (or get dropped).
      // StarterKit (v3) bundles the Link extension, so configure it here rather
      // than adding a second Link (a duplicate lets StarterKit's default win,
      // which opens/navigates the href on click — breaking in-editor link editing).
      // It bundles Underline and Strike too — registering `@tiptap/extension-
      // underline` alongside it is a duplicate extension name, which Tiptap warns
      // about and which leaves two marks competing for the same `<u>`.
      StarterKit.configure({
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: { openOnClick: false },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // NOT-221: ProseMirror decoration on the empty doc — always in sync with
      // document state (incl. programmatic setContent), rendered inside the
      // first line so it never overlaps real content.
      Placeholder.configure({ placeholder }),
      // Email designer only: inline color + font family via TextStyle marks,
      // and images (the company logo, inserted as an <img> merge field).
      ...(extended
        ? [
            TextStyle,
            Color,
            FontFamily.configure({ types: ['textStyle'] }),
            FontSize.configure({ types: ['textStyle'] }),
            // Inline so the image lives inside a paragraph and the existing
            // left/center/right align buttons position it.
            Image.configure({ inline: true, allowBase64: false }),
          ]
        : []),
    ],
    content: richTextEditorContent(value),
    editable,
    onUpdate({ editor }) {
      onChange(editor.isEmpty ? '' : editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          'min-h-[120px] outline-none px-3 py-2 text-sm',
          RICH_TEXT_CLASS,
          editorClassName,
        ),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(id ? { id } : {}),
        // Render the content in the delivered medium's font (e.g. the email base
        // stack) so the editor matches the sent message, not the app UI font.
        ...(contentFontFamily ? { style: `font-family:${contentFontFamily}` } : {}),
      },
      handleKeyDown(_view, event) {
        // ⌘/Ctrl-K opens the link editor, matching Slack/Docs/Word.
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          openLinkRef.current();
          return true;
        }
        return false;
      },
    },
  });

  // Keep Tiptap's editable state in sync when the prop flips.
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  // Sync external value changes (e.g. edit mode load)
  useEffect(() => {
    if (!editor) return;
    const content = richTextEditorContent(value);
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [value, editor]);

  // Hand the editor instance to a caller that needs imperative commands.
  useEffect(() => {
    onEditorReady?.(editor);
    return () => onEditorReady?.(null);
  }, [editor, onEditorReady]);

  const closeLink = useCallback(() => {
    setLinkPos(null);
    setLinkUrl('');
  }, []);

  // Open the link popover anchored just under the current selection, prefilled
  // with the existing link's href when the cursor sits on one.
  const openLinkEditor = useCallback(() => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const container = containerRef.current;
    let pos = { top: 8, left: 8 };
    if (container) {
      const caret = editor.view.coordsAtPos(from);
      const box = container.getBoundingClientRect();
      // Clamp so a selection near the right edge keeps the popover in view.
      const left = Math.max(8, Math.min(caret.left - box.left, box.width - 320));
      pos = { top: caret.bottom - box.top + 6, left };
    }
    setLinkUrl((editor.getAttributes('link').href as string) ?? '');
    setLinkPos(pos);
    requestAnimationFrame(() => linkInputRef.current?.focus());
  }, [editor]);
  openLinkRef.current = openLinkEditor;

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      // Empty submit clears the link the selection is on (if any).
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      // No selection and not on a link → drop the URL in as clickable text.
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url } }],
        })
        .insertContent(' ')
        .run();
    } else {
      // Link the selection, or update the whole link the cursor is inside.
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    closeLink();
  }, [editor, linkUrl, closeLink]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLink();
  }, [editor, closeLink]);

  // Dismiss the popover on an outside click (clicks within it are ignored).
  useEffect(() => {
    if (!linkPos) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeLink();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [linkPos, closeLink]);

  if (!editor) return null;

  return (
    <div ref={containerRef} className={cn('relative rounded-md border bg-background', className)}>
      {/* Toolbar — hidden in read-only mode */}
      {editable ? (
        <div
          className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1"
          onMouseDown={(e) => e.preventDefault()}
        >
          <Toggle
            size="sm"
            pressed={editor.isActive('bold')}
            onPressedChange={() => editor.chain().focus().toggleBold().run()}
            aria-label="Bold"
          >
            <Bold className="size-3.5" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('italic')}
            onPressedChange={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Italic"
          >
            <Italic className="size-3.5" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('underline')}
            onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Underline"
          >
            <UnderlineIcon className="size-3.5" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('strike')}
            onPressedChange={() => editor.chain().focus().toggleStrike().run()}
            aria-label="Strikethrough"
          >
            <Strikethrough className="size-3.5" />
          </Toggle>

          {showFullToolbar ? (
            <>
              <div className="mx-1 h-4 w-px bg-border" />

              <Toggle
                size="sm"
                pressed={editor.isActive({ textAlign: 'left' })}
                onPressedChange={() => editor.chain().focus().setTextAlign('left').run()}
                aria-label="Align left"
              >
                <AlignLeft className="size-3.5" />
              </Toggle>
              <Toggle
                size="sm"
                pressed={editor.isActive({ textAlign: 'center' })}
                onPressedChange={() => editor.chain().focus().setTextAlign('center').run()}
                aria-label="Align center"
              >
                <AlignCenter className="size-3.5" />
              </Toggle>
              <Toggle
                size="sm"
                pressed={editor.isActive({ textAlign: 'right' })}
                onPressedChange={() => editor.chain().focus().setTextAlign('right').run()}
                aria-label="Align right"
              >
                <AlignRight className="size-3.5" />
              </Toggle>

              <div className="mx-1 h-4 w-px bg-border" />

              <Toggle
                size="sm"
                pressed={editor.isActive('heading', { level: 1 })}
                onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                aria-label="Heading 1"
              >
                <Heading1 className="size-3.5" />
              </Toggle>
              <Toggle
                size="sm"
                pressed={editor.isActive('heading', { level: 2 })}
                onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                aria-label="Heading 2"
              >
                <Heading2 className="size-3.5" />
              </Toggle>
            </>
          ) : null}

          <div className="mx-1 h-4 w-px bg-border" />

          <Toggle
            size="sm"
            pressed={editor.isActive('bulletList')}
            onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
            aria-label="Bullet list"
          >
            <List className="size-3.5" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('orderedList')}
            onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
            aria-label="Ordered list"
          >
            <ListOrdered className="size-3.5" />
          </Toggle>

          {extended ? (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              {/* Font family */}
              <select
                aria-label="Font"
                className="h-7 rounded border bg-background px-1 text-xs outline-none"
                // The toolbar prevents mousedown (to keep the editor selection);
                // stop it here so this native control can still open.
                onMouseDown={(e) => e.stopPropagation()}
                // No explicit mark ⇒ the base font (Inter), so show Inter selected.
                value={editor.getAttributes('textStyle').fontFamily ?? 'Inter, sans-serif'}
                onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
              >
                {EMAIL_FONTS.map(([value, label]) => (
                  <option key={label} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {/* Text size */}
              <select
                aria-label="Text size"
                className="h-7 rounded border bg-background px-1 text-xs outline-none"
                onMouseDown={(e) => e.stopPropagation()}
                value={editor.getAttributes('textStyle').fontSize ?? ''}
                onChange={(e) => {
                  const size = e.target.value;
                  if (size) editor.chain().focus().setFontSize(size).run();
                  else editor.chain().focus().unsetFontSize().run();
                }}
              >
                {EMAIL_SIZES.map(([value, label]) => (
                  <option key={label} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {/* Text color */}
              <label
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded"
                title="Text color"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <span
                  className="h-3.5 w-3.5 rounded-full border border-border"
                  style={{
                    background: (editor.getAttributes('textStyle').color as string) ?? '#111827',
                  }}
                />
                <input
                  type="color"
                  className="sr-only"
                  value={(editor.getAttributes('textStyle').color as string) ?? '#111827'}
                  onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                />
              </label>
            </>
          ) : null}

          <div className="mx-1 h-4 w-px bg-border" />

          <Toggle
            size="sm"
            pressed={editor.isActive('link') || linkPos !== null}
            onPressedChange={() => (linkPos ? closeLink() : openLinkEditor())}
            aria-label="Link"
          >
            <Link2 className="size-3.5" />
          </Toggle>

          {toolbarEnd && <div className="ml-auto">{toolbarEnd}</div>}
        </div>
      ) : null}

      {/* Link editor — floating popover anchored to the selection */}
      {linkPos && (
        <div
          className="absolute z-20 flex w-[320px] items-center gap-1.5 rounded-lg border bg-popover p-1.5 shadow-md"
          style={{ top: linkPos.top, left: linkPos.left }}
          // Keep the editor selection while interacting with the popover.
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            ref={linkInputRef}
            className="h-7 min-w-0 flex-1 rounded border bg-background px-2 text-xs outline-none focus:border-ring"
            placeholder="Paste or type a link, then Enter"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                closeLink();
                editor.chain().focus().run();
              }
            }}
          />
          <button
            type="button"
            onClick={applyLink}
            className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            {linkUrl.trim() ? 'Apply' : 'Clear'}
          </button>
          {editor.isActive('link') ? (
            <button
              type="button"
              onClick={removeLink}
              aria-label="Remove link"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Unlink className="size-3.5" />
            </button>
          ) : null}
        </div>
      )}

      {/* Editor area */}
      <EditorContent editor={editor} />
      {footer ? <div className="border-t px-3 py-2">{footer}</div> : null}
    </div>
  );
}
